import { Injectable } from '@angular/core';
import { environment, injectProduction } from '@y42/core/environment/src';
import { RudderAnalyticsService } from './rudder-analytics.service';
import { UserTrackingSettingsService } from './user-tracking-settings.service';
import { TrackingEvent, UserTraits } from './user-tracking.model';

@Injectable()
export class UserTrackingService {
  constructor(
    private readonly userTrackingSettingsService: UserTrackingSettingsService,
    private readonly rudder: RudderAnalyticsService,
  ) {}

  private readonly production = injectProduction();
  private initialized = false;

  initialize() {
    if (this.initialized) {
      return;
    }

    if (this.canCall()) {
      this.rudder.load({
        writeKey: environment.rudderstack.writeKey,
        dataplaneUrl: 'https://dataplane.rs.y42.dev/',
        configUrl: 'https://api.rs.y42.dev/',
      });
    }

    this.initialized = true;
  }

  /**
   * Called once after login.
   *
   * Can be called subsequent times to set/override user traits.
   *
   * https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#traits
   */
  identify(traits?: UserTraits) {
    if (!this.canCall()) {
      return;
    }

    const { id, ...user } = this.userTrackingSettingsService.getUser();
    return this.rudder.identify(id, { ...user, ...(traits || {}) });
  }

  track(event: TrackingEvent) {
    if (!this.canCall()) {
      return;
    }

    return this.rudder.track(
      event.name,
      {
        ...this.userTrackingSettingsService.getBaseProperties(),
        ...event.properties,
      },
      event.traits
        ? {
            traits: event.traits,
          }
        : undefined,
    );
  }

  page() {
    if (!this.canCall()) {
      return;
    }

    return this.rudder.page('product', this.userTrackingSettingsService.getBaseProperties());
  }

  private canCall() {
    return this.production;
  }
}
